import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, forkJoin } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { ApplyTimeOffComponent } from './apply/apply.component';
import { CancelConfirmationComponent } from './cancel-confirmation/cancel-confirmation.component';
import { AuthService } from '../shared/services/auth.service';
import { BackendService } from '../shared/services/backend.service';
import { TimeOffInfoComponent } from '../shared/components/time-off-info/time-off-info.component';
import { TimeOffGroup } from '../shared/models/static-data.model';
import { AppliedTimeOff, ApplyTimeOffRequest, TimeOffAudit, TimeOffSummary } from '../shared/models/time-off.model';

@Injectable({
  providedIn: 'root'
})
export class TimeOffService {
  overviewLoading$ = new BehaviorSubject<boolean>(true);
  overviewDetails$ = new BehaviorSubject<TimeOffSummary>({} as TimeOffSummary);

  auditLoading$ = new BehaviorSubject<boolean>(true);
  auditDetails$ = new BehaviorSubject<TimeOffAudit[]>([]);

  pendingForReviewLoading$ = new BehaviorSubject<boolean>(true);
  pendingForReviewDetails$ = new BehaviorSubject<AppliedTimeOff[]>([]);

  historyLoading$ = new BehaviorSubject<boolean>(true);
  historyDetails$ = new BehaviorSubject<AppliedTimeOff[]>([]);

  employeeEmail: string | undefined = '';

  applicableLeavesDetails$ = new BehaviorSubject<string[]>([]);

  constructor(
    private backendService: BackendService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  getTimeOffOverview() {
    this.overviewLoading$.next(true);
    const email = this.getEmail();

    this.backendService.getTimeOffOverview(email).pipe(
      tap((response: TimeOffSummary) => {
        this.overviewLoading$.next(false);
        this.overviewDetails$.next(response);
      }),
      catchError((error) => {
        this.overviewLoading$.next(false);
        this.overviewDetails$.next({} as TimeOffSummary);
        throw error;
      })
    ).subscribe({ error: () => {} });
  }

  getTimeOffAudit() {
    this.auditLoading$.next(true);

    const email = this.getEmail();

    this.backendService.getTimeOffAudit(email).subscribe({
      next: (response: TimeOffAudit[]) => {
        this.auditLoading$.next(false);
        this.auditDetails$.next(response);
      },
      error: () => {
        this.auditLoading$.next(false);
      }
    });
  }

  getTimeOffList() {
    this.pendingForReviewLoading$.next(true);
    const email = this.getEmail();

    this.backendService.getTimeOffList(email).pipe(
      tap((response: AppliedTimeOff[]) => {
        this.pendingForReviewLoading$.next(false);
        this.pendingForReviewDetails$.next(response);
      }),
      catchError((error) => {
        this.pendingForReviewLoading$.next(false);
        this.pendingForReviewDetails$.next([]);
        throw error;
      })
    ).subscribe({ error: () => {} });
  }

  openCancelConfirmationDialogBox(timeOffId: string) {
    return this.dialog.open(CancelConfirmationComponent, {
      data: {
        timeOffId
      }
    });
  }

  openApplyTimeOffDialogBox() {
    this.dialog.open(ApplyTimeOffComponent, {
      width: '1000px'
    });
  }

  openTimeOffDetailsDialogBox(timeOff: AppliedTimeOff, mode: string) {
    this.dialog.open(TimeOffInfoComponent, {
      width: '1000px',
      data: {
        mode: mode,
        timeOff
      }
    });
  }

  openTimeOffAuditDialogBox(timeOff: AppliedTimeOff) {
    this.dialog.open(TimeOffInfoComponent, {
      width: '1000px',
      data: {
        mode: 'AUDIT',
        timeOff
      }
    });
  }

  applyTimeOff(body: ApplyTimeOffRequest) {
    const email = this.getEmail();

    return this.backendService.applyTimeOff(email, body);
  }
  
  cancelTimeOff(timeOffId: string, dialogRef: MatDialogRef<CancelConfirmationComponent>) {
    const email = this.getEmail();

    this.backendService.cancelTimeOff(timeOffId, email).subscribe({
      next: (response: string) => {
        dialogRef.close();

        this.snackBar.open(response, '✖', {
          duration: 5000,
          panelClass: ['green-snackbar'],
        });

        this.loadAllData();
      },
      error: (error) => {
        // Re-enable the dialog so the user can retry
        if (dialogRef.componentInstance) {
          dialogRef.componentInstance.loading = false;
        }
        if (error?.status === 400) {
          return; // BackendService already shows the API's message (e.g. no longer pending)
        }
        this.snackBar.open('Failed to cancel time-off. Please try again.', '✖', {
          duration: 5000,
          panelClass: ['red-snackbar'],
        });
      }
    });
  }

  getHistory() {
    this.historyLoading$.next(true);
    const email = this.getEmail();

    this.backendService.getTimeOffHistory(email).pipe(
      tap((response: AppliedTimeOff[]) => {
        this.historyLoading$.next(false);
        this.historyDetails$.next(response);
      }),
      catchError((error) => {
        this.historyLoading$.next(false);
        this.historyDetails$.next([]);
        throw error;
      })
    ).subscribe({ error: () => {} });
  }

  loadAllData() {
    const email = this.getEmail();

    this.historyLoading$.next(true);
    this.pendingForReviewLoading$.next(true);
    this.overviewLoading$.next(true);

    forkJoin({
      history: this.backendService.getTimeOffHistory(email),
      list: this.backendService.getTimeOffList(email),
      overview: this.backendService.getTimeOffOverview(email)
    }).subscribe({
      next: (results) => {
        this.historyLoading$.next(false);
        this.historyDetails$.next(results.history);

        this.pendingForReviewLoading$.next(false);
        this.pendingForReviewDetails$.next(results.list);

        this.overviewLoading$.next(false);
        this.overviewDetails$.next(results.overview);
      },
      error: () => {
        this.historyLoading$.next(false);
        this.historyDetails$.next([]);
        this.pendingForReviewLoading$.next(false);
        this.pendingForReviewDetails$.next([]);
        this.overviewLoading$.next(false);
        this.overviewDetails$.next({} as TimeOffSummary);
      }
    });
  }

  getEmail() {
    if (this.employeeEmail && this.employeeEmail !== '') {
        return this.employeeEmail;
    }
    
    try {
        return this.authService.getUserSessionData('EMAIL');
    } catch (error) {
        console.error('Failed to get email from session:', error);
        return '';
    }
  }

  getTagDisplayName(tag: string) {
    try {
        const timeOffTypes = this.authService.getStaticData('TIMEOFF_TYPES');
        let friendlyName = '';
        
        (timeOffTypes).forEach((group: TimeOffGroup) => {
          if(group.tag === tag) {
            friendlyName = group.friendly_name;
          }
        });

        return friendlyName;
    } catch (error) {
        console.error('Failed to get time-off types from static data:', error);
        return tag;
    }
  }

  getTimeOffCount(type: string) {
    let allAvailableLeaves = Object.entries(this.overviewDetails$.value);
    let currentAvailableLeave = 0;

    for(let i = 0; i < allAvailableLeaves.length; i++) {
      if(allAvailableLeaves[i][0] === type) {
        currentAvailableLeave = allAvailableLeaves[i][1];
      }
    }

    return currentAvailableLeave;
  }

  approveTimeOff(timeOff: AppliedTimeOff, comments: any) {
    return this.backendService.putTimeOffAction(timeOff.MEMBER_ID, timeOff.TIMEOFF_ID, 'APPROVED', comments);
  }

  rejectTimeOff(timeOff: AppliedTimeOff, comments: any) {
    return this.backendService.putTimeOffAction(timeOff.MEMBER_ID, timeOff.TIMEOFF_ID, 'REJECTED', comments);
  }
}
